import awsmobile from '../config/aws-exports';
import type { HistoricSystemDataRecord } from '../types/charts';
import {
  AdditionalOnboardingInformation,
  AnalyticsDataItem,
  AnalyticsRangeResponse,
  Direction,
  HardwareType,
  InverterAuthDetails,
  OnboardingTraceRecord,
  OptimiserSettings,
  OptimiseUserDetails,
  SupportedHardwareResponse,
  TOUCosts,
  UpdateUserDetails,
  UserBatterySolarDetails,
} from '../types/optimise';
import apiRequest, { UserSub } from '../utils/apiRequest';

/**
 * Constant representing the kilowatts generated per solar panel.
 *
 * This value is used to calculate the total energy output of the solar panels
 * based on the number of panels a user has.
 */
export const KW_PER_PANEL = 0.4;

/** Hardware types used in the onboarding process and hardware-related API requests. */
export const HardwareTypes = {
  INVERTER: 'inverter',
  BATTERY: 'battery',
} as const satisfies Record<string, HardwareType>;

/** Tariff directions used by tariff-related API requests. */
export const TariffDirection = {
  IMPORT: 'import',
  EXPORT: 'export',
} as const satisfies Record<string, Direction>;

/**
 * @constant {string} tdasCampaignId
 * @description A unique identifier for the TDAS (Turn Down & Save) campaign.
 * This value is used throughout the application to reference the campaign in APIs,
 * analytics, or campaign-specific logic.
 */
export const tdasCampaignId = 'LO_TDAS';

/**
 * Creates user battery solar details.
 *
 * Sends a POST request to create the user's battery solar details.
 *
 * @async
 * @function createUserBatterySolarDetails
 * @param {Object} details - The battery solar details to be saved.
 * @returns {Promise<any>} - A promise that resolves to the response from the API.
 */
export const createUserBatterySolarDetails = async (details: UserBatterySolarDetails) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/onboarding/user-battery-solar-details`, null, 'POST', details);

/**
 * Creates user trace.
 *
 * Sends a POST request to log the user's progress through the onboarding process.
 *
 * @async
 * @function createUserTrace
 * @param {string} stepCompleted - The step of the onboarding process that has been completed.
 * @param {Object} [data={}] - Additional data related to the completed step.
 * @returns {Promise<any>} - A promise that resolves to the response from the API.
 */
export const createUserTrace = async (stepCompleted: string, data = {}) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/onboarding/user-trace`, null, 'POST', {
    step_completed: stepCompleted,
    data,
  });

/**
 * Retrieves supported hardware.
 *
 * Sends a GET request to retrieve the list of supported hardware for a given hardware type.
 *
 * @async
 * @function getSupportedHardware
 * @param {string} hardwareType - The type of hardware for which to retrieve support information. Can be either "inverter" or "battery".
 * @returns {Promise<SupportedHardwareResponse>} - A promise that resolves to the response from the API containing the supported hardware.
 */
export const getSupportedHardware = async (hardwareType: HardwareType) =>
  await apiRequest<SupportedHardwareResponse>(
    `${awsmobile.optimise_endpoint_auth}/onboarding/supported-hardware?hardware_type=${hardwareType}`
  );

/**
 * Retrieves 24 hours of system data.
 *
 * Retrieves 24 hours of system data for a given user.
 *
 * @async
 * @function getTwentyHoursOfSystemData
 * @param {string|null} [userSub=null] - Optional user sub identifier.
 * @returns {Promise<any>} - A promise that resolves to the system data.
 */
export const getTwentyHoursOfSystemData = async (userSub: UserSub = null) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/24-hours-of-system-data`, userSub);

/**
 * Retrieves analytics data.
 *
 * Fetches analytics data for a given date.
 *
 * @async
 * @function getAnalyticsData
 * @param {string} date - The date for which to retrieve analytics data (format: YYYY-MM-DD).
 * @param {string|null} [userSub=null] - Optional user sub identifier.
 * @returns {Promise<AnalyticsDataItem[]>} - A promise that resolves to the analytics data.
 */
export const getAnalyticsData = async (date: string, userSub: UserSub = null) => {
  const res = await apiRequest<AnalyticsDataItem[] | string>(
    `${awsmobile.optimise_endpoint_auth}/analytics-data?analysis_date=${date}`,
    userSub
  );

  if (!res || typeof res === 'string') {
    throw new Error(res || 'Invalid analytics data response');
  }

  return res;
};

/**
 * Retrieves an analytics summary for a date range at a given resolution.
 *
 * @param resolution - Bucket size: "day" returns half-hourly; "week"/"month" return daily; "year" returns monthly.
 * @param start - ISO date string (YYYY-MM-DD), inclusive.
 * @param end - ISO date string (YYYY-MM-DD), inclusive.
 * @param userSub - Optional user sub identifier.
 */
export const getAnalyticsSummary = async (
  resolution: 'day' | 'week' | 'month' | 'year',
  start: string,
  end: string,
  userSub: UserSub = null
): Promise<AnalyticsRangeResponse> => {
  const res = await apiRequest<AnalyticsRangeResponse | string>(
    `${awsmobile.optimise_endpoint_auth}/analytics-summary?resolution=${resolution}&start=${start}&end=${end}`,
    userSub
  );

  if (!res || typeof res === 'string') {
    throw new Error(res || 'Invalid analytics summary response');
  }

  return res;
};

/**
 * @async
 * @param {string} direction - The direction of the tariff, one of the `TariffDirection` properties.
 * @param {string} regionId - The region identifier for which to fetch supported tariffs. As returned by the Location service getRegion call.
 * @return {Promise<any>} - A promise that resolves to the supported tariffs. - A promise that resolves to the supported tariffs.
 */
export const getSupportedTariffs = async (direction: Direction, regionId: string) =>
  await apiRequest(
    `${awsmobile.optimise_endpoint_auth}/onboarding/supported-tariffs?direction=${direction}&region=${regionId}`
  );

/**
 * Retrieves the current behaviour forecast.
 *
 * Fetches the current behaviour forecast for the user.
 *
 * @async
 * @function getCurrentBehaviourForecast
 * @param {string|null} [userSub=null] - Optional user sub identifier.
 * @returns {Promise<any>} - A promise that resolves to the behaviour forecast data.
 */
export const getCurrentBehaviourForecast = async (userSub: UserSub = null) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/current-behaviour-forecast`, userSub);

/**
 * Retrieves user details.
 *
 * Retrieves the user's details from the server.
 *
 * @async
 * @function getUserDetails
 * @param {string|null} [userSub=null] - Optional user sub identifier.
 * @returns {Promise<any>} - A promise that resolves to the user details.
 */
export const getUserDetails = async (userSub: UserSub = null) => {
  const user = await apiRequest<OptimiseUserDetails>(`${awsmobile.optimise_endpoint_auth}/user-details`, userSub);

  if (!user || typeof user === 'string') {
    throw new Error(user || 'Failed to fetch user details');
  }

  // Compute has_smart_tariff based on the presence of import and export tariff
  const has_smart_tariff = Boolean(user?.tariffs?.export_tariff && user?.tariffs?.import_tariff);

  return {
    ...user,
    has_smart_tariff,
  };
};

/**
 * Updates user details.
 *
 * Sends a POST request to update the user's details.
 *
 * @async
 * @function updateUserDetails
 * @param {Object} details - The user details to be updated.
 * @param {string|null} [userSub=null] - Optional user sub identifier.
 * @returns {Promise<any>} - A promise that resolves to the server response.
 */
export const updateUserDetails = async (details: UpdateUserDetails, userSub: UserSub = null) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/user-details`, userSub, 'POST', details);

/**
 * Creates inverter authentication details.
 *
 * Sends a PUT request to create or update the user's inverter authentication details.
 *
 * @async
 * @function createInverterAuthentication
 * @param {Object} details - The inverter authentication details to be updated.
 * @param {string|null} [userSub=null] - Optional user sub identifier, required so this targets the
 *   impersonated customer (not the caller's own JWT identity) when used from an admin session.
 * @returns {Promise<any>} - A promise that resolves to the server response.
 */
export const createInverterAuthentication = async (details: InverterAuthDetails, userSub: UserSub = null) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/inverter-authentication`, userSub, 'PUT', details);

/**
 * Retrieves the hardware details captured during onboarding (currently battery and inverter
 * manufacturer/model), used by the Settings "Inverter details" panel.
 *
 * This endpoint is HTTP_PROXY (unlike other endpoints in this file it does no server-side
 * transformation), so the response is the raw array of onboarding trace records - the frontend
 * extracts `hardware_details` from the first record itself.
 *
 * @async
 * @function getAdditionalInformation
 * @param {string} userSub - The user's sub identifier, required as a query param by this endpoint.
 * @returns {Promise<AdditionalOnboardingInformation>} - A promise that resolves to the additional onboarding information.
 */
export const getAdditionalInformation = async (userSub: string): Promise<AdditionalOnboardingInformation> => {
  const res = await apiRequest<OnboardingTraceRecord[] | string>(
    `${awsmobile.optimise_endpoint_auth}/onboarding/additional-information?user_sub=${userSub}`,
    userSub
  );

  if (!res || typeof res === 'string') {
    throw new Error(res || 'Failed to fetch additional onboarding information');
  }

  return {
    hardware_details: res[0]?.additional_onboarding_information?.hardware_details ?? null,
  };
};

/**
 * Retrieves user loop account.
 *
 * Sends a GET request to retrieve the user's loop account details.
 *
 * @async
 * @function getUserLoopAccount
 * @returns {Promise<any>} - A promise that resolves to the loop account details.
 */
export const getUserLoopAccount = async () =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/internal/user-details/loop-account`);

/**
 * Retrieves user mpan.
 *
 * Sends a GET request to retrieve the user's mpan.
 *
 * @async
 * @function getUserMpan
 * @returns {Promise<any>} - A promise that resolves to the user mpan.
 */
export const getUserMpan = async () => await apiRequest(`${awsmobile.optimise_endpoint_auth}/internal/user/mpan`);

/**
 * Retrieves all campaigns.
 *
 * Sends a GET request to retrieve all campaigns.
 *
 * @async
 * @function getCampaigns
 * @returns {Promise<any>} - A promise that resolves to the list of all campaigns.
 */
export const getGridCampaigns = async () =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/grid-event/campaigns`);

/**
 * Retrieves all user campaigns.
 *
 * Sends a GET request to retrieve all campaigns for a specific user.
 *
 * @async
 * @function getUserCampaigns
 * @returns {Promise<any>} - A promise that resolves to the list of user-specific campaigns.
 */
export const getUserGridCampaigns = async () =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/grid-event/campaigns/user`);

/**
 * Sets the user's campaign participation status.
 *
 * Sends a POST request to update the user's participation status for a specific campaign.
 *
 * @async
 * @function setUserCampaigns
 * @param {string} campaignId - The ID of the campaign to set for the user.
 * @param {boolean} [optIn=true] - The user's opt-in status for the campaign. Defaults to `true`.
 * @returns {Promise<any>} - A promise that resolves to the response of the API request.
 */
export const setUserGridCampaigns = async (campaignId: string, optIn: boolean = true) =>
  // TODO: Remove the query params when the api accepts a post body in future
  await apiRequest(
    `${awsmobile.optimise_endpoint_auth}/grid-event/campaigns/user?campaign_id=${campaignId}&opt_in=${optIn}`,
    null,
    'POST',
    { campaign_id: campaignId, opt_in: optIn }
  );

/**
 * Get all grid events for a campaign that a user is entering, these can be all
 * historic events in the past or future events that the user will be entering
 *
 * @async
 * @function getUserEvents
 * @param {string} campaignId - The ID of the campaign to set for the user.
 * @returns {Promise<any>} - A promise that resolves to the response of the API request.
 */
export const getUserGridEvents = async (campaignId: string) =>
  await apiRequest(
    `${awsmobile.optimise_endpoint_auth}/grid-event/campaigns/user/entered-events?campaign_id=${campaignId}`
  );

/**
 * Get optimiser settings for a user
 *
 * Fetches the optimiser settings for a given user. This includes self-consumption mode settings.
 *
 * @param userSub
 * @returns {Promise<undefined|*>}
 */
export const getOptimiserSettings = async (userSub: UserSub = null) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/optimiser/settings`, userSub);

/**
 * Update optimiser settings for a user
 *
 * Sends a POST request to update the optimiser settings for a given user.
 *
 * @param optimiserSettings
 * @param userSub
 * @returns {Promise<undefined|*>}
 */
export const updateOptimiserSettings = async (optimiserSettings: OptimiserSettings, userSub: UserSub) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/optimiser/settings`, userSub, 'POST', optimiserSettings);

/**
 * Retrieves historic system data.
 *
 * Sends a GET request to retrieve historic system data for a user within two timestamps.
 *
 * @async
 * @function getHistoricSystemData
 * @param {number} startTimestamp - The start timestamp in UTC.
 * @param {number} endTimestamp - The end timestamp in UTC.
 * @returns Rows of historic system data for the requested UTC window.
 */
export const getHistoricSystemData = async (
  startTimestamp: number,
  endTimestamp: number,
  userSub: UserSub = null
): Promise<HistoricSystemDataRecord[]> => {
  const res = await apiRequest<HistoricSystemDataRecord[]>(
    `${awsmobile.optimise_endpoint_auth}/historic-system-data?start_timestamp_utc=${startTimestamp}&end_timestamp_utc=${endTimestamp}`,
    userSub
  );
  return Array.isArray(res) ? res : [];
};

/**
 * Fetches time-of-use (TOU) costs for a given tariff over a specific time range.
 *
 * @async
 * @function getTariffsTOUCosts
 * @param {number} startTimestamp - The start of the time range (UTC Unix timestamp in seconds).
 * @param {number} endTimestamp - The end of the time range (UTC Unix timestamp in seconds).
 * @param {string} tariffId - The ID of the tariff for which TOU costs are being fetched.
 * @returns {Promise<TOUCosts>} A promise that resolves to an object containing the cost windows for the requested tariff.
 *
 * @example
 * const touCosts = await getTariffsTOUCosts(1690000000, 1690604800, "GB|20|IMPORT|BRITISH_GAS|EDv12");
 */
export const getTariffsTOUCosts = async (
  startTimestamp: number,
  endTimestamp: number,
  tariffId: string
): Promise<TOUCosts> => {
  const res = await apiRequest<TOUCosts>(
    `${awsmobile.optimise_endpoint_auth}/tariffs/tou-costs?start_timestamp_utc=${startTimestamp}&end_timestamp_utc=${endTimestamp}&tariff_id=${encodeURIComponent(tariffId)}`
  );

  if (!res || typeof res === 'string') {
    throw new Error(res || 'Unexpected API response');
  }

  return res;
};

/**
 * Fetches the recommended tariffs for a specific user based on their user subscription ID.
 *
 * @async
 * @function getRecommendedTariffs
 * @param {string} userSub - The user's subscription identifier.
 * @returns {Promise<RecommendedTariffs>} A promise that resolves to the user's recommended tariff results.
 *
 * @example
 * const recommended = await getRecommendedTariffs("user-123-sub-id");
 */
export const getRecommendedTariffs = async (userSub: string) =>
  await apiRequest(`${awsmobile.optimise_endpoint_auth}/tariff-sim/user/results?user_sub=${userSub}`, userSub);
